import axios from "axios";
import { ApiLink } from "../../../../APILink/ApiLink";
import authHeader from "../../../../Auth/authHeader";

const api = axios.create({
  baseURL: ApiLink.API_URL,
  headers: authHeader()
});

const safeNum = (value) => (typeof value === "number" && !Number.isNaN(value) ? value : Number(value) || 0);
const normalizeFiscalYear = (value) => {
  const parsed = Number(value);
  if (Number.isInteger(parsed) && parsed > 0) return parsed;
  return new Date().getFullYear();
};
const isNonEmptyArray = (value) => Array.isArray(value) && value.length > 0;
const hasModernFinancialPayload = (payload) => {
  if (!payload || typeof payload !== "object") return false;
  return (
    Array.isArray(payload.rows) ||
    payload.summary !== undefined ||
    Array.isArray(payload.balanceOverTime) ||
    Array.isArray(payload.monthlyMovement) ||
    typeof payload.total === "number" ||
    typeof payload.totalRows === "number"
  );
};

const normalizeDate = (value) => {
  if (!value) return "";
  const text = String(value);
  if (text.includes("T")) return text.split("T")[0];
  if (text.includes(" ")) return text.split(" ")[0];
  return text;
};

const toDateOnly = (value) => normalizeDate(value).replaceAll("/", "-");

const buildSortParam = (sortKey, sortDir) => {
  const key = String(sortKey || "date").trim() || "date";
  const dir = String(sortDir || "desc").toLowerCase() === "asc" ? "asc" : "desc";
  return `${key}:${dir}`;
};

const getLegacyRows = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.rows)) return payload.rows;
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
};

const toRangeParams = (dateRange) => {
  const params = {};
  if (dateRange?.from) params.fromDate = dateRange.from;
  if (dateRange?.to) params.toDate = dateRange.to;
  return params;
};

const mapFinancialRows = (rows, openingBalance = 0) => {
  let runningBalance = openingBalance;

  return (rows || []).map((row, index) => {
    const debit = safeNum(row.debit ?? row.Debit);
    const credit = safeNum(row.credit ?? row.Credit);
    runningBalance += debit - credit;

    return {
      id: row.id ?? row.JournalID ?? index + 1,
      journalID: row.journalID ?? row.JournalID ?? 0,
      date: row.date ?? row.Date ?? "",
      documentNo: row.documentNo ?? row.DocumentNo ?? row.journalID ?? row.JournalID ?? "—",
      accountName:
        row.accountName ??
        row.AccountName ??
        row.accName ??
        row.AccName ??
        "",
      identificationNumber:
        row.identificationNumber ??
        row.IdentificationNumber ??
        row.idNumber ??
        row.IdNumber ??
        row.NUI ??
        row.nui ??
        "",
      actionType: row.actionType ?? row.ActionType ?? row.type ?? row.Type ?? "",
      description: row.description ?? row.Comment ?? row.comment ?? "",
      debit,
      credit,
      currency: row.currency ?? row.Currency ?? "ALL",
      status: row.status ?? row.ActionType ?? (credit > 0 ? "paid" : "open"),
      runningBalance
    };
  });
};

export async function fetchClientAccounts({ ledgerID = 105, search = "", page = 1, pageSize = 50 } = {}) {
  let response;
  try {
    response = await api.get("Accounts/accountByLedger", {
      params: { ledger: ledgerID }
    });
  } catch {
    response = await api.get("Accounts/accountsPick", {
      params: { ledgerID, search, page, pageSize }
    });
  }

  return (response.data || []).map((item) => ({
    id: item.AccountID ?? item.accountID ?? item.AccountId ?? item.accountId ?? item.id,
    AccountID: item.AccountID ?? item.accountID ?? item.AccountId ?? item.accountId ?? item.id,
    AccountName: item.AccountName ?? item.accountName ?? "",
    IdentificationNumber: item.IdentificationNumber ?? item.identificationNumber ?? "",
    VATNumber: item.VATNumber ?? item.vatNumber ?? "",
    Adress: item.Adress ?? item.adress ?? "",
    LedgerNameEN: item.LedgerNameEN ?? item.ledgerNameEN ?? "",
    IsActive: item.IsActive ?? item.isActive ?? true
  }));
}

export async function listFinancialTransactionsPage(req) {
  const fiscalYear = normalizeFiscalYear(req.fiscalYear);
  try {
    const response = await api.get("ClientCard/financial/dashboard", {
      params: {
        accountId: req.clientId,
        fiscalYear,
        page: req.page || 1,
        pageSize: req.pageSize || 25,
        sort: buildSortParam(req.sortKey, req.sortDir),
        search: req.filters?.search || "",
        status: req.filters?.status || "all",
        currency: req.filters?.currency || "",
        ...toRangeParams(req.filters?.dateRange)
      }
    });

    if (hasModernFinancialPayload(response.data)) {
      const rows = mapFinancialRows(response.data?.rows || [], response.data?.summary?.openingBalance || 0);
      const total = response.data?.total ?? response.data?.totalRows ?? rows.length;
      return { rows, total };
    }
  } catch (error) {
    console.warn("ClientCard financial dashboard failed; falling back to legacy Card endpoint.", error);
  }

  const legacyResponse = await api.get("Card/getKartelaFinanciareAnalitike", {
    params: {
      fiscalYear,
      accountID: req.clientId
    }
  });

  const legacyRows = getLegacyRows(legacyResponse.data);
  const normalizedLegacyRows = mapFinancialRows(legacyRows, 0);
  const page = req.page || 1;
  const pageSize = req.pageSize || 25;
  const start = (page - 1) * pageSize;
  const end = start + pageSize;

  return {
    rows: normalizedLegacyRows.slice(start, end),
    total: normalizedLegacyRows.length
  };
}

export async function computeFinancialKpis(clientId, dateRange, fiscalYear) {
  const normalizedFiscalYear = normalizeFiscalYear(fiscalYear);
  try {
    const response = await api.get("ClientCard/financial/dashboard", {
      params: {
        accountId: clientId,
        fiscalYear: normalizedFiscalYear,
        ...toRangeParams(dateRange)
      }
    });

    const summary = response.data?.summary ?? response.data?.Summary;
    if (summary !== undefined) {
      return {
        openingBalance: safeNum(summary.openingBalance ?? summary.OpeningBalance),
        totalDebit: safeNum(summary.totalDebit ?? summary.TotalDebit),
        totalCredit: safeNum(summary.totalCredit ?? summary.TotalCredit),
        currentBalance: safeNum(summary.currentBalance ?? summary.CurrentBalance),
        overdueAmount: safeNum(summary.overdueAmount ?? summary.OverdueAmount),
        paymentTermsDays: Number(summary.paymentTermsDays ?? summary.PaymentTermsDays ?? 0),
        lastTransactionDate: summary.lastTransactionDate ?? summary.LastTransactionDate ?? null,
        paidCount: Number(summary.paidCount ?? summary.PaidCount ?? 0),
        unpaidCount: Number(summary.unpaidCount ?? summary.UnpaidCount ?? 0)
      };
    }
  } catch (error) {
    console.warn("ClientCard KPI request failed; falling back to legacy Card endpoint.", error);
  }

  const legacyResponse = await api.get("Card/getKartelaFinanciareAnalitike", {
    params: {
      fiscalYear: normalizedFiscalYear,
      accountID: clientId
    }
  });

  const legacyRows = getLegacyRows(legacyResponse.data);
  const fromDate = toDateOnly(dateRange?.from);
  const toDate = toDateOnly(dateRange?.to);
  const rowsBeforeRange = fromDate
    ? legacyRows.filter((row) => {
        const rowDate = toDateOnly(row.Date ?? row.date);
        return rowDate && rowDate < fromDate;
      })
    : legacyRows.filter((row) => String(row.ActionType ?? row.actionType).toUpperCase() === "ACS");
  const periodRows = legacyRows.filter((row) => {
    const rowDate = toDateOnly(row.Date ?? row.date);
    const isOpeningEntry = String(row.ActionType ?? row.actionType).toUpperCase() === "ACS";
    if (isOpeningEntry) return false;
    if (fromDate && (!rowDate || rowDate < fromDate)) return false;
    if (toDate && (!rowDate || rowDate > toDate)) return false;
    return true;
  });
  const openingBalance = rowsBeforeRange.reduce(
    (sum, row) => sum + safeNum(row.Debit ?? row.debit) - safeNum(row.Credit ?? row.credit),
    0
  );
  const totalDebit = periodRows.reduce((sum, row) => sum + safeNum(row.Debit ?? row.debit), 0);
  const totalCredit = periodRows.reduce((sum, row) => sum + safeNum(row.Credit ?? row.credit), 0);
  const currentBalance = openingBalance + totalDebit - totalCredit;

  return {
    openingBalance,
    totalDebit,
    totalCredit,
    currentBalance,
    overdueAmount: 0,
    paymentTermsDays: 30,
    lastTransactionDate: periodRows?.[0]?.Date || periodRows?.[0]?.date || null,
    paidVsUnpaid: {
      paid: periodRows.filter((row) => safeNum(row.Credit ?? row.credit) > 0).length,
      unpaid: periodRows.filter((row) => safeNum(row.Credit ?? row.credit) <= 0).length
    }
  };
}

export async function getFinancialChartSeries(clientId, dateRange, fiscalYear) {
  const normalizedFiscalYear = normalizeFiscalYear(fiscalYear);
  try {
    const response = await api.get("ClientCard/financial/dashboard", {
      params: {
        accountId: clientId,
        fiscalYear: normalizedFiscalYear,
        ...toRangeParams(dateRange)
      }
    });

    const rawBalanceOverTime = response.data?.balanceOverTime ?? response.data?.BalanceOverTime ?? [];
    const rawMonthlyMovement = response.data?.monthlyMovement ?? response.data?.MonthlyMovement ?? [];
    if (
      Array.isArray(response.data?.balanceOverTime) ||
      Array.isArray(response.data?.BalanceOverTime) ||
      Array.isArray(response.data?.monthlyMovement) ||
      Array.isArray(response.data?.MonthlyMovement)
    ) {
      return {
        balanceOverTime: rawBalanceOverTime.map((item) => ({
          date: item?.date ?? item?.Date ?? "",
          balance: safeNum(item?.balance ?? item?.Balance),
          debit: safeNum(item?.debit ?? item?.Debit),
          credit: safeNum(item?.credit ?? item?.Credit)
        })),
        monthlyMovement: rawMonthlyMovement.map((item) => ({
          month: item?.month ?? item?.Month ?? "",
          debit: safeNum(item?.debit ?? item?.Debit),
          credit: safeNum(item?.credit ?? item?.Credit)
        }))
      };
    }
  } catch (error) {
    console.warn("ClientCard chart request failed; falling back to legacy Card endpoint.", error);
  }

  const legacyRowsResponse = await api.get("Card/getKartelaFinanciareAnalitike", {
    params: {
      fiscalYear: normalizedFiscalYear,
      accountID: clientId
    }
  });

  const legacyRows = getLegacyRows(legacyRowsResponse.data);
  const fromDate = toDateOnly(dateRange?.from);
  const toDate = toDateOnly(dateRange?.to);
  const isOpeningEntry = (row) => String(row.ActionType ?? row.actionType).toUpperCase() === "ACS";
  const openingBalance = legacyRows.reduce((sum, row) => {
    const rowDate = toDateOnly(row.Date ?? row.date);
    const belongsToOpening = isOpeningEntry(row) || (fromDate && rowDate && rowDate < fromDate);
    return belongsToOpening
      ? sum + safeNum(row.Debit ?? row.debit) - safeNum(row.Credit ?? row.credit)
      : sum;
  }, 0);
  const periodRows = legacyRows
    .filter((row) => {
      const rowDate = toDateOnly(row.Date ?? row.date);
      if (isOpeningEntry(row)) return false;
      if (fromDate && (!rowDate || rowDate < fromDate)) return false;
      if (toDate && (!rowDate || rowDate > toDate)) return false;
      return true;
    })
    .sort((a, b) => {
      const dateCompare = toDateOnly(a.Date ?? a.date).localeCompare(toDateOnly(b.Date ?? b.date));
      if (dateCompare !== 0) return dateCompare;
      return safeNum(a.JournalID ?? a.journalID) - safeNum(b.JournalID ?? b.journalID);
    });

  let balance = openingBalance;
  const balanceOverTime = periodRows.map((item) => {
    const debit = safeNum(item.Debit ?? item.debit);
    const credit = safeNum(item.Credit ?? item.credit);
    balance += debit - credit;
    return {
      date: normalizeDate(item.Date ?? item.date),
      balance,
      debit,
      credit
    };
  });

  const monthlyMap = new Map();
  periodRows.forEach((item) => {
    const month = toDateOnly(item.Date ?? item.date).slice(0, 7);
    if (!month) return;
    const current = monthlyMap.get(month) || { month, debit: 0, credit: 0 };
    current.debit += safeNum(item.Debit ?? item.debit);
    current.credit += safeNum(item.Credit ?? item.credit);
    monthlyMap.set(month, current);
  });
  const monthlyMovement = Array.from(monthlyMap.values()).sort((a, b) => a.month.localeCompare(b.month));

  return {
    balanceOverTime,
    monthlyMovement
  };
}

export async function computeMaterialSynthetic(clientId, dateRange, fiscalYear) {
  const normalizedFiscalYear = normalizeFiscalYear(fiscalYear);
  try {
    const response = await api.get("ClientCard/material/synthetic", {
      params: {
        accountID: clientId,
        fiscalYear: normalizedFiscalYear,
        ...toRangeParams(dateRange)
      }
    });

    const salesSummary = response.data?.salesSummary || response.data?.SalesSummary;
    if (salesSummary) {
      return {
        totalSoldQty: safeNum(salesSummary.totalSoldQty ?? salesSummary.TotalSoldQty),
        totalReturnedQty: safeNum(salesSummary.totalReturnedQty ?? salesSummary.TotalReturnedQty),
        netSoldQty: safeNum(salesSummary.netSoldQty ?? salesSummary.NetSoldQty),
        totalSalesValue: safeNum(salesSummary.totalSalesValue ?? salesSummary.TotalSalesValue),
        averageUnitSalesValue: safeNum(salesSummary.averageUnitSalesValue ?? salesSummary.AverageUnitSalesValue),
        distinctSoldArticles: Number((salesSummary.distinctSoldArticles ?? salesSummary.DistinctSoldArticles) || 0),
        activeSalesMonths: Number((salesSummary.activeSalesMonths ?? salesSummary.ActiveSalesMonths) || 0),
        monthlyAverageSoldQty: safeNum(salesSummary.monthlyAverageSoldQty ?? salesSummary.MonthlyAverageSoldQty)
      };
    }

    const summary = response.data?.summary;
    if (summary) return summary;
  } catch {
    // Fall back to legacy source.
  }

  const legacyResponse = await api.get("Card/getKartelaMaterialeSintetike", {
    params: {
      fiscalYear: normalizedFiscalYear,
      accountID: clientId
    }
  });

  const rows = legacyResponse.data || [];
  const totalSoldQty = rows.reduce((sum, item) => sum + safeNum(item.TotalQtyOutput), 0);
  const totalSalesValue = rows.reduce((sum, item) => sum + safeNum(item.TotalAmount), 0);
  const averageUnitSalesValue = totalSoldQty > 0 ? totalSalesValue / totalSoldQty : 0;
  const distinctSoldArticles = rows.reduce((count, item) => count + (safeNum(item.TotalQtyOutput) > 0 ? 1 : 0), 0);

  return {
    totalSoldQty,
    totalReturnedQty: 0,
    netSoldQty: totalSoldQty,
    totalSalesValue,
    averageUnitSalesValue,
    distinctSoldArticles,
    activeSalesMonths: 0,
    monthlyAverageSoldQty: 0
  };
}

export async function getMaterialSyntheticSeries(clientId, dateRange, fiscalYear) {
  const normalizedFiscalYear = normalizeFiscalYear(fiscalYear);
  console.warn("[MaterialSyntheticSeries] function called", {
    clientId,
    fiscalYear: normalizedFiscalYear,
    dateRange
  });
  const normalizeSeriesPoint = (item) => ({
    date: item?.date ?? item?.Date ?? "",
    stock: safeNum(item?.stock ?? item?.Stock),
    soldQtyCumulative: safeNum(item?.soldQtyCumulative ?? item?.SoldQtyCumulative),
    salesValueCumulative: safeNum(item?.salesValueCumulative ?? item?.SalesValueCumulative)
  });

  const normalizeMonthlyPoint = (item) => ({
    month: item?.month ?? item?.Month ?? "",
    soldQty: safeNum(item?.soldQty ?? item?.SoldQty ?? item?.outQty ?? item?.OutQty),
    returnedQty: safeNum(item?.returnedQty ?? item?.ReturnedQty ?? item?.inQty ?? item?.InQty),
    salesValue: safeNum(item?.salesValue ?? item?.SalesValue ?? item?.invValue ?? item?.InvValue),
    outQty: safeNum(item?.outQty ?? item?.OutQty ?? item?.soldQty ?? item?.SoldQty),
    inQty: safeNum(item?.inQty ?? item?.InQty ?? item?.returnedQty ?? item?.ReturnedQty),
    invValue: safeNum(item?.invValue ?? item?.InvValue ?? item?.salesValue ?? item?.SalesValue)
  });

  const toIsoDate = (value) => {
    const text = String(value || "").trim();
    if (!text) return "";

    if (/^\d{4}-\d{2}-\d{2}$/.test(text)) return text;

    const dashDmy = text.match(/^(\d{2})-(\d{2})-(\d{4})$/);
    if (dashDmy) return `${dashDmy[3]}-${dashDmy[2]}-${dashDmy[1]}`;

    const slashDmy = text.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
    if (slashDmy) return `${slashDmy[3]}-${slashDmy[2]}-${slashDmy[1]}`;

    const parsed = new Date(text);
    if (Number.isNaN(parsed.getTime())) return "";
    return parsed.toISOString().slice(0, 10);
  };

  const buildSeriesFromLegacy = async () => {
    const syntheticResp = await api.get("Card/getKartelaMaterialeSintetike", {
      params: {
        fiscalYear: normalizedFiscalYear,
        accountID: clientId
      }
    });

    const syntheticRows = Array.isArray(syntheticResp.data) ? syntheticResp.data : [];
    if (!syntheticRows.length) {
      return { stockOverTime: [], salesOverTime: [], monthly: [] };
    }

    const articleIds = syntheticRows
      .map((item) => Number(item?.ArticleID || item?.articleID || 0))
      .filter((id) => Number.isInteger(id) && id > 0);

    if (!articleIds.length) {
      return { stockOverTime: [], salesOverTime: [], monthly: [] };
    }

    const historyResponses = await Promise.all(
      articleIds.map(async (articleId) => {
        try {
          return await api.get("ClientCard/material/analytical/history", {
            params: {
              fiscalYear: normalizedFiscalYear,
              accountID: clientId,
              articleID: articleId,
              ...toRangeParams(dateRange)
            }
          });
        } catch {
          return api.get("Card/getKartelaMaterialeAnalitike", {
            params: {
              fiscalYear: normalizedFiscalYear,
              accountID: clientId,
              articleID: articleId
            }
          });
        }
      })
    );

    const historyRows = historyResponses.flatMap((resp) => (Array.isArray(resp.data) ? resp.data : []));
    if (!historyRows.length) {
      return { stockOverTime: [], salesOverTime: [], monthly: [] };
    }

    const normalizedHistory = historyRows
      .map((row) => ({
        date: toIsoDate(row?.Date ?? row?.date),
        actionType: String(row?.ActionType ?? row?.actionType ?? row?.type ?? "").trim().toUpperCase(),
        unit: String(row?.ArticleUnitName ?? row?.articleUnitName ?? row?.UnitName ?? row?.unitName ?? "").trim(),
        soldQty: safeNum(row?.QtyOutput ?? row?.qtyOutput),
        salesValue: safeNum(row?.TotalAmount ?? row?.totalAmount)
      }))
      .filter((row) => row.date)
      .sort((a, b) => a.date.localeCompare(b.date));

    if (!normalizedHistory.length) {
      return { stockOverTime: [], salesOverTime: [], monthly: [] };
    }

    const salesOverTime = [];
    let soldCum = 0;
    let valueCum = 0;
    normalizedHistory.forEach((row) => {
      soldCum += row.soldQty;
      valueCum += row.salesValue;
      salesOverTime.push({
        date: row.date,
        stock: 0,
        soldQtyCumulative: soldCum,
        salesValueCumulative: valueCum
      });
    });

    const monthlyMap = new Map();
    normalizedHistory.forEach((row) => {
      const month = row.date.slice(0, 7);
      if (!monthlyMap.has(month)) {
        monthlyMap.set(month, {
          month,
          soldQty: 0,
          returnedQty: 0,
          salesValue: 0,
          outQty: 0,
          inQty: 0,
          invValue: 0
        });
      }
      const bucket = monthlyMap.get(month);
      bucket.soldQty += row.soldQty;
      bucket.outQty += row.soldQty;
      bucket.salesValue += row.salesValue;
      bucket.invValue += row.salesValue;
    });

    const monthly = Array.from(monthlyMap.values()).sort((a, b) => a.month.localeCompare(b.month));
    const materialSaleTypes = new Set(["IND", "POS", "ZRP", "DND"]);
    const unitMonthlyMap = new Map();
    normalizedHistory
      .filter((row) => materialSaleTypes.has(row.actionType) && row.soldQty > 0 && row.unit)
      .forEach((row) => {
        const month = row.date.slice(0, 7);
        const key = `${month}::${row.unit}`;
        const current = unitMonthlyMap.get(key) || { month, unit: row.unit, soldQty: 0 };
        current.soldQty += row.soldQty;
        unitMonthlyMap.set(key, current);
      });
    const unitMonthly = Array.from(unitMonthlyMap.values()).sort(
      (a, b) => a.month.localeCompare(b.month) || a.unit.localeCompare(b.unit)
    );

    return {
      stockOverTime: salesOverTime,
      salesOverTime,
      monthly,
      unitMonthly
    };
  };

  try {
    console.warn("[MaterialSyntheticSeries] requesting ClientCard/material/synthetic");
    const response = await api.get("ClientCard/material/synthetic", {
      params: {
        accountID: clientId,
        fiscalYear: normalizedFiscalYear,
        ...toRangeParams(dateRange)
      }
    });

    console.warn("[ClientCard/material/synthetic] response:", response.data);
    console.warn(
      "[ClientCard/material/synthetic] unitMonthly:",
      response.data?.unitMonthly ?? response.data?.UnitMonthly
    );

    const rawSalesOverTime = response.data?.salesOverTime || response.data?.SalesOverTime || [];
    const rawStockOverTime = response.data?.stockOverTime || response.data?.StockOverTime || [];
    const rawMonthly = response.data?.monthly || response.data?.Monthly || [];
    const rawUnitMonthly = response.data?.unitMonthly || response.data?.UnitMonthly || [];

    const salesOverTime = (rawSalesOverTime || []).map(normalizeSeriesPoint);
    const stockOverTime = (rawStockOverTime || []).map(normalizeSeriesPoint);
    const monthly = (rawMonthly || []).map(normalizeMonthlyPoint);
    const unitMonthly = (rawUnitMonthly || []).map((item) => ({
      month: item?.month ?? item?.Month ?? "",
      unit: item?.unit ?? item?.Unit ?? "Pa njësi",
      soldQty: safeNum(item?.soldQty ?? item?.SoldQty)
    }));

    if (isNonEmptyArray(salesOverTime) || isNonEmptyArray(monthly)) {
      let resolvedUnitMonthly = unitMonthly;
      if (!resolvedUnitMonthly.length) {
        try {
          const fallbackSeries = await buildSeriesFromLegacy();
          resolvedUnitMonthly = fallbackSeries?.unitMonthly || [];
        } catch {
          resolvedUnitMonthly = [];
        }
      }
      return {
        stockOverTime,
        salesOverTime,
        monthly,
        unitMonthly: resolvedUnitMonthly
      };
    }

    if (isNonEmptyArray(stockOverTime) || isNonEmptyArray(monthly)) {
      return {
        stockOverTime,
        salesOverTime: stockOverTime,
        monthly,
        unitMonthly
      };
    }
  } catch (error) {
    console.error("[MaterialSyntheticSeries] modern endpoint failed:", error);
    try {
      const fallbackResult = await buildSeriesFromLegacy();
      console.warn("[MaterialSyntheticSeries] fallback result:", fallbackResult);
      return fallbackResult;
    } catch (fallbackError) {
      console.error("[MaterialSyntheticSeries] fallback failed:", fallbackError);
      // Ignore and return empty below.
    }
  }

  try {
    return await buildSeriesFromLegacy();
  } catch {
    return {
      stockOverTime: [],
      salesOverTime: [],
      monthly: [],
      unitMonthly: []
    };
  }
}

export async function listMaterialSyntheticRowsPage(req) {
  const fiscalYear = normalizeFiscalYear(req?.fiscalYear);

  const mapRows = (source) =>
    (source || []).map((item, index) => ({
      id: item.id ?? index + 1,
      articleId: Number(item.articleId ?? item.ArticleID ?? 0),
      barcode: item.barcode ?? item.Barcode ?? "",
      articleName: item.articleName ?? item.ArticleName ?? "",
      totalTransactions: safeNum(item.totalTransactions ?? item.TotalTransactions),
      totalQtyOutput: safeNum(item.totalQtyOutput ?? item.TotalQtyOutput),
      avgPriceWithoutVAT: safeNum(item.avgPriceWithoutVAT ?? item.AvgPriceWithoutVAT),
      totalAmount: safeNum(item.totalAmount ?? item.TotalAmount),
      rowInfo: item
    }));

  try {
    const response = await api.get("ClientCard/material/synthetic/rows", {
      params: {
        accountID: req.clientId,
        fiscalYear,
        page: req.page || 1,
        pageSize: req.pageSize || 25,
        search: req.filters?.search || "",
        sort: req.sort || "",
        ...toRangeParams(req.filters?.dateRange)
      }
    });

    const rows = mapRows(response.data?.rows || response.data?.Rows || response.data || []);
    const total = Number(response.data?.totalRows ?? response.data?.TotalRows ?? rows.length);
    if (rows.length > 0 || total > 0) {
      return { rows, total };
    }
  } catch {
    // Fall back to legacy list from Card/getKartelaMaterialeSintetike.
  }

  const legacyResponse = await api.get("Card/getKartelaMaterialeSintetike", {
    params: {
      fiscalYear,
      accountID: req.clientId
    }
  });

  const legacyRows = mapRows(legacyResponse.data || []);
  const searchTerm = String(req.filters?.search || "").trim().toLowerCase();
  const searched = searchTerm
    ? legacyRows.filter((row) => `${row.barcode} ${row.articleName}`.toLowerCase().includes(searchTerm))
    : legacyRows;

  const page = req.page || 1;
  const pageSize = req.pageSize || 25;
  const start = (page - 1) * pageSize;
  const end = start + pageSize;

  return {
    rows: searched.slice(start, end),
    total: searched.length
  };
}

export async function computeMaterialAnalytical(clientId, opts) {
  const fiscalYear = normalizeFiscalYear(opts?.fiscalYear);
  const mapAnalyticalRows = (source) =>
    (source || []).map((item, index) => ({
      id: item.id ?? index + 1,
      itemId: item.itemId ?? item.articleID ?? item.ArticleID ?? index + 1,
      itemCode: item.itemCode ?? item.barcode ?? item.Barcode ?? "",
      itemName: item.itemName ?? item.articleName ?? item.ArticleName ?? "",
      category: item.category ?? item.articleGroupName ?? item.ArticleGroupName ?? "",
      unit: item.unit ?? item.articleUnitName ?? item.ArticleUnitName ?? "",
      warehouse: item.warehouse ?? item.WarehouseName ?? "",
      qtyIn: safeNum(item.qtyIn ?? item.QtyIn),
      qtyOut: safeNum(item.qtyOut ?? item.TotalQtyOutput ?? item.QtyOutput),
      balance: safeNum(item.balance ?? item.Balance),
      unitCostAvg: safeNum(item.unitCostAvg ?? item.AvgPriceWithoutVAT ?? item.UnitCostAvg),
      totalValue: safeNum(item.totalValue ?? item.TotalAmount ?? item.TotalValue)
    }));

  try {
    const response = await api.get("ClientCard/material/analytical", {
      params: {
        accountID: clientId,
        fiscalYear,
        search: opts?.search || "",
        stockStatus: opts?.stockStatus || "all",
        warehouse: opts?.warehouse || "",
        category: opts?.category || "",
        ...toRangeParams(opts?.dateRange)
      }
    });

    const rows = mapAnalyticalRows(response.data?.rows || response.data?.items || response.data || []);
    if (rows.length > 0) return rows;
  } catch {
    // Fall back to legacy material synthetic source for per-item analytical list.
  }

  const legacyResponse = await api.get("Card/getKartelaMaterialeSintetike", {
    params: {
      fiscalYear,
      accountID: clientId
    }
  });

  return mapAnalyticalRows(legacyResponse.data || []);
}

export async function listMaterialAnalyticalRowsPage(req) {
  const fiscalYear = normalizeFiscalYear(req?.fiscalYear);
  const mapRows = (source) =>
    (source || []).map((item, index) => ({
      id: item.id ?? index + 1,
      itemId: item.itemId ?? item.articleID ?? item.ArticleID ?? index + 1,
      itemCode: item.itemCode ?? item.barcode ?? item.Barcode ?? "",
      itemName: item.itemName ?? item.articleName ?? item.ArticleName ?? "",
      category: item.category ?? item.articleGroupName ?? item.ArticleGroupName ?? "",
      unit: item.unit ?? item.articleUnitName ?? item.ArticleUnitName ?? "",
      warehouse: item.warehouse ?? item.WarehouseName ?? "",
      qtyIn: safeNum(item.qtyIn ?? item.QtyIn),
      qtyOut: safeNum(item.qtyOut ?? item.TotalQtyOutput ?? item.QtyOutput),
      balance: safeNum(item.balance ?? item.Balance),
      unitCostAvg: safeNum(item.unitCostAvg ?? item.AvgPriceWithoutVAT ?? item.UnitCostAvg),
      totalValue: safeNum(item.totalValue ?? item.TotalAmount ?? item.TotalValue),
      rowInfo: item
    }));

  try {
    const response = await api.get("ClientCard/material/analytical/rows", {
      params: {
        accountID: req.clientId,
        fiscalYear,
        page: req.page || 1,
        pageSize: req.pageSize || 25,
        sort: req.sort || "",
        filterModel: JSON.stringify(req.filterItems || []),
        search: req.filters?.search || "",
        category: req.filters?.category || "",
        stockStatus: req.filters?.stockStatus || "all",
        ...toRangeParams(req.filters?.dateRange)
      }
    });

    const rows = mapRows(response.data?.rows || response.data?.Rows || []);
    const total = Number(response.data?.totalRows ?? response.data?.TotalRows ?? rows.length);
    if (rows.length > 0 || total > 0) {
      return { rows, total };
    }
  } catch {
    // fall back below
  }

  const allRows = await computeMaterialAnalytical(req.clientId, {
    dateRange: req.filters?.dateRange,
    fiscalYear,
    search: req.filters?.search || "",
    category: req.filters?.category || "",
    stockStatus: req.filters?.stockStatus || "all"
  });

  const page = req.page || 1;
  const pageSize = req.pageSize || 25;
  const start = (page - 1) * pageSize;
  const end = start + pageSize;

  return {
    rows: mapRows(allRows).slice(start, end),
    total: (allRows || []).length
  };
}

export async function getMaterialAnalyticalCharts(clientId, opts = {}) {
  const fiscalYear = normalizeFiscalYear(opts?.fiscalYear);

  try {
    const response = await api.get("ClientCard/material/analytical/charts", {
      params: {
        accountID: clientId,
        fiscalYear,
        search: opts?.search || "",
        stockStatus: opts?.stockStatus || "all",
        category: opts?.category || "",
        ...toRangeParams(opts?.dateRange)
      }
    });

    const data = response.data || {};
    return {
      topTraded: data.topTraded || data.TopTraded || [],
      topBySalesValue: data.topBySalesValue || data.TopBySalesValue || [],
      monthlyTrend: data.monthlyTrend || data.MonthlyTrend || [],
      categoryDistribution: data.categoryDistribution || data.CategoryDistribution || []
    };
  } catch {
    const legacy = await getTopMaterialInsights(clientId, opts?.dateRange, fiscalYear);

    const [analyticalRows, syntheticSeries] = await Promise.all([
      computeMaterialAnalytical(clientId, {
        dateRange: opts?.dateRange,
        fiscalYear,
        search: opts?.search,
        category: opts?.category,
        stockStatus: opts?.stockStatus
      }),
      getMaterialSyntheticSeries(clientId, opts?.dateRange, fiscalYear)
    ]);

    const categoryMap = new Map();
    (analyticalRows || []).forEach((row) => {
      const categoryName = String(row?.category || "Pa kategori").trim() || "Pa kategori";
      if (!categoryMap.has(categoryName)) {
        categoryMap.set(categoryName, {
          category: categoryName,
          soldQty: 0,
          salesValue: 0
        });
      }
      const bucket = categoryMap.get(categoryName);
      bucket.soldQty += safeNum(row?.qtyOut);
      bucket.salesValue += safeNum(row?.totalValue);
    });

    const categoryDistribution = Array.from(categoryMap.values())
      .sort((a, b) => b.salesValue - a.salesValue)
      .slice(0, 10);

    const monthlyTrend = (syntheticSeries?.monthly || []).map((item) => ({
      month: item?.month ?? item?.Month ?? "",
      soldQty: safeNum(item?.soldQty ?? item?.SoldQty),
      salesValue: safeNum(item?.salesValue ?? item?.SalesValue),
      transactions: 0
    }));

    return {
      topTraded: legacy?.topTraded || [],
      topBySalesValue: legacy?.mostProfitable || [],
      monthlyTrend,
      categoryDistribution
    };
  }
}

export async function getTopMaterialInsights(clientId, dateRange, fiscalYear) {
  const normalizedFiscalYear = normalizeFiscalYear(fiscalYear);
  try {
    const response = await api.get("ClientCard/material/analytical", {
      params: {
        accountID: clientId,
        fiscalYear: normalizedFiscalYear,
        ...toRangeParams(dateRange)
      }
    });

    const topTraded = response.data?.topTraded || [];
    const mostProfitable = response.data?.mostProfitable || [];
    if (isNonEmptyArray(topTraded) || isNonEmptyArray(mostProfitable)) {
      return { topTraded, mostProfitable };
    }
  } catch {
    // Fall back to insights from legacy synthetic list.
  }

  const legacyResponse = await api.get("Card/getKartelaMaterialeSintetike", {
    params: {
      fiscalYear: normalizedFiscalYear,
      accountID: clientId
    }
  });

  const rows = (legacyResponse.data || []).map((item) => ({
    code: item.Barcode || "",
    name: item.ArticleName || "",
    qty: safeNum(item.TotalQtyOutput),
    profit: safeNum(item.TotalAmount),
    itemId: item.ArticleID
  }));

  return {
    topTraded: [...rows].sort((a, b) => b.qty - a.qty).slice(0, 10),
    mostProfitable: [...rows].sort((a, b) => b.profit - a.profit).slice(0, 10)
  };
}

export async function getMaterialAnalyticalHistory(clientId, articleId, opts = {}) {
  const fiscalYear = normalizeFiscalYear(opts?.fiscalYear);
  const mapHistory = (source) =>
    (source || []).map((item, index) => ({
      id: item.id ?? item.HistoryID ?? index + 1,
      date: item.date ?? item.Date ?? "",
      documentNo: item.documentNo ?? item.JournalID ?? item.HistoryID ?? "",
      type: item.type ?? item.ActionType ?? "",
      quantity: safeNum(item.quantity ?? ((item.qtyOutput ?? item.QtyOutput ?? 0) > 0
        ? (item.qtyOutput ?? item.QtyOutput)
        : -1 * (item.qtyInput ?? item.QtyInput ?? 0))),
      qtyInput: safeNum(item.qtyInput ?? item.QtyInput),
      qtyOutput: safeNum(item.qtyOutput ?? item.QtyOutput),
      unitCost: safeNum(item.unitCost ?? item.priceWithoutVAT ?? item.PriceWithoutVAT ?? item.priceWithVAT ?? item.PriceWithVAT),
      totalAmount: safeNum(item.totalAmount ?? item.TotalAmount),
      barcode: item.barcode ?? item.Barcode ?? "",
      articleName: item.articleName ?? item.ArticleName ?? ""
    }));

  try {
    const response = await api.get("ClientCard/material/analytical/history", {
      params: {
        accountID: clientId,
        articleID: articleId,
        fiscalYear,
        ...toRangeParams(opts?.dateRange)
      }
    });

    const rows = mapHistory(response.data?.items || response.data || []);
    if (rows.length > 0) return rows;
  } catch {
    // Fall back to legacy history endpoint.
  }

  const legacyResponse = await api.get("Card/getKartelaMaterialeAnalitike", {
    params: {
      fiscalYear,
      accountID: clientId,
      articleID: articleId
    }
  });

  return mapHistory(legacyResponse.data || []);
}

export { normalizeDate };
